if (!customElements.get('sticky-buy-bar')) {
  customElements.define(
    'sticky-buy-bar',
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('.sbb__bar');
        this.productInfo = this.closest('product-info');
        this._savedScrollY = 0;

        // Restore scroll position after variant navigation
        this.restoreScrollPosition();

        // Show/hide bar based on main ATC button visibility
        this.setupVisibilityObserver();

        // Wire up interactivity
        this.setupATC();
        this.setupSwatchNavigation();
        this.setupSwatchPrefetch();
        this.setupMobileSheet();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.footerObserver?.disconnect();
        document.removeEventListener('keydown', this.handleKeydown);
      }

      // ─── Scroll restoration ──────────────────────────────────────────────

      restoreScrollPosition() {
        // Handled by the inline script in theme.liquid <head>, which consumes
        // sbb-scroll-y immediately on page load. This is a fallback only.
        const savedY = sessionStorage.getItem('sbb-scroll-y');
        if (savedY === null) return;

        sessionStorage.removeItem('sbb-scroll-y');
        const targetY = parseInt(savedY, 10);
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

        const reveal = () => {
          requestAnimationFrame(() => {
            window.scrollTo({ top: targetY, behavior: 'instant' });
            document.documentElement.style.transition = 'opacity 200ms ease';
            requestAnimationFrame(() => {
              document.documentElement.style.opacity = '1';
              document.documentElement.addEventListener(
                'transitionend',
                () => {
                  document.documentElement.style.transition = '';
                  document.documentElement.style.opacity = '';
                },
                { once: true }
              );
            });
          });
        };

        if (document.readyState === 'complete') {
          reveal();
        } else {
          window.addEventListener('load', reveal, { once: true });
        }
      }

      // ─── Visibility (IntersectionObserver) ──────────────────────────────

      setupVisibilityObserver() {
        if (!this.bar || !this.productInfo) return;

        const productForm = this.productInfo.querySelector('product-form');
        if (!productForm) return;

        this._atcHidden = false;
        this._footerVisible = false;
        let initialized = false;

        const updateBar = () => {
          if (!initialized) return;
          const show = this._atcHidden && !this._footerVisible && window.scrollY > 0;
          this.bar.classList.toggle('sbb--visible', show);
          // WCAG 2.4.11: keep focused elements visible above the sticky bar
          document.documentElement.style.scrollPaddingBottom = show ? `${this.bar.offsetHeight}px` : '';
          if (show) {
            this._savedScrollY = window.scrollY;
          }
        };

        this.observer = new IntersectionObserver(
          ([entry]) => {
            this._atcHidden = !entry.isIntersecting;
            updateBar();
          },
          { threshold: 0 }
        );
        this.observer.observe(productForm);

        const footer = document.querySelector('footer.footer');
        if (footer) {
          this.footerObserver = new IntersectionObserver(
            ([entry]) => {
              this._footerVisible = entry.isIntersecting;
              updateBar();
            },
            { threshold: 0 }
          );
          this.footerObserver.observe(footer);
        }

        // Wait for all resources to load so layout is stable before enabling
        // visibility. The double-rAF wasn't long enough on mobile — images loading
        // above the form can temporarily push it out of the viewport, causing a
        // false-positive show on load.
        const initVisibility = () => requestAnimationFrame(() => {
          initialized = true;
          updateBar();
        });

        if (document.readyState === 'complete') {
          initVisibility();
        } else {
          window.addEventListener('load', initVisibility, { once: true });
        }
      }

      // ─── ATC delegation ─────────────────────────────────────────────────

      setupATC() {
        const atcButton = this.querySelector('[data-sbb-atc]');
        if (!atcButton) return;

        if (this.hasAttribute('data-bundle')) {
          atcButton.addEventListener('click', () => {
            const bundleSelector = document.querySelector('.bundle-selector');
            if (bundleSelector) {
              const top = bundleSelector.getBoundingClientRect().top + window.scrollY - 150;
              window.scrollTo({ top, behavior: 'smooth' });
            }
          });
        } else {
          atcButton.addEventListener('click', () => {
            const form = this.productInfo?.querySelector('product-form form');
            if (form) form.requestSubmit();
          });
        }
      }

      // ─── Swatch navigation + scroll save ────────────────────────────────

      setupSwatchNavigation() {
        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-sbb-swatch-link], [data-sbb-sheet-link]');
          if (!link) return;

          // Don't navigate to the current product
          if (link.getAttribute('aria-current') === 'page') {
            event.preventDefault();
            return;
          }

          event.preventDefault();

          // Disconnect the observer BEFORE navigating so the bar doesn't hide
          // itself if the browser briefly scrolls to 0 during page unload.
          this.observer?.disconnect();
          this.bar?.classList.add('sbb--visible');

          // Desktop swatch links: use live window.scrollY at click time (accurate).
          // Mobile sheet links: use _savedScrollY captured before the sheet opened,
          // because iOS Safari returns 0 for window.scrollY when a fixed overlay is visible.
          const isSheetLink = !!event.target.closest('[data-sbb-sheet-link]');
          const scrollY = isSheetLink ? this._savedScrollY : window.scrollY;
          sessionStorage.setItem('sbb-scroll-y', scrollY);

          document.documentElement.style.transition = 'opacity 150ms ease';
          document.documentElement.style.opacity = '0';
          setTimeout(() => window.location.assign(link.href), 160);
        });
      }

      // ─── Hover prefetch ──────────────────────────────────────────────────

      setupSwatchPrefetch() {
        this.addEventListener('pointerenter', (event) => {
          const link = event.target.closest('[data-sbb-swatch-link], [data-sbb-sheet-link]');
          if (!link || link.getAttribute('aria-current') === 'page') return;
          if (document.head.querySelector(`link[rel="prefetch"][href="${link.href}"]`)) return;
          const hint = document.createElement('link');
          hint.rel = 'prefetch';
          hint.as = 'document';
          hint.href = link.href;
          document.head.appendChild(hint);
        }, true);
      }

      // ─── Mobile bottom sheet ─────────────────────────────────────────────

      setupMobileSheet() {
        this.sheet = this.querySelector('.sbb__sheet');
        this.backdrop = this.querySelector('.sbb__backdrop');
        this.trigger = this.querySelector('.sbb__mobile-trigger');
        this.closeBtn = this.querySelector('[data-sbb-close]');

        if (!this.sheet || !this.trigger) return;

        // Don't set up sheet if there are no variants to show
        if (this.trigger.getAttribute('aria-disabled') === 'true') return;

        this.handleKeydown = (event) => {
          if (event.key === 'Escape') this.closeSheet();
        };

        this.trigger.addEventListener('click', () => this.openSheet());
        this.closeBtn?.addEventListener('click', () => this.closeSheet());
        this.backdrop?.addEventListener('click', () => this.closeSheet());
      }

      openSheet() {
        if (!this.sheet || !this.backdrop) return;

        // Snapshot scroll before the sheet opens — iOS Safari returns 0 for
        // window.scrollY once a fixed overlay is visible.
        this._savedScrollY = window.scrollY;

        this.sheet.removeAttribute('hidden');
        this.backdrop.removeAttribute('hidden');
        this.sheet.setAttribute('aria-hidden', 'false');
        this.trigger.setAttribute('aria-expanded', 'true');

        requestAnimationFrame(() => {
          this.sheet.classList.add('sbb__sheet--open');
          this.backdrop.classList.add('sbb__backdrop--visible');
        });

        document.addEventListener('keydown', this.handleKeydown);

        if (typeof trapFocus === 'function') {
          trapFocus(this.sheet);
        }
      }

      closeSheet() {
        if (!this.sheet || !this.backdrop) return;

        this.sheet.classList.remove('sbb__sheet--open');
        this.backdrop.classList.remove('sbb__backdrop--visible');
        this.sheet.setAttribute('aria-hidden', 'true');
        this.trigger.setAttribute('aria-expanded', 'false');

        document.removeEventListener('keydown', this.handleKeydown);

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.trigger);
        }

        const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300;
        setTimeout(() => {
          this.sheet.setAttribute('hidden', '');
          this.backdrop.setAttribute('hidden', '');
        }, duration);
      }
    }
  );
}
